import useUsers from '@/hooks/use-users';

import PaddockAvatar from '@/components/ui/paddock-avatar/paddock-avatar';
import getCallMeBy from '@/components/user/get-call-me-by';
import { cn } from '@/lib/utils';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

type SuggestionItem = {
  id: string;
  name: string;
};

type MentionListProps = {
  items: SuggestionItem[];
  command: ({
    id,
    label,
  }: {
    id: string;
    label: { type: string; name: string };
  }) => void;
};

export const MentionList = forwardRef((props: MentionListProps, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { users } = useUsers();

  const selectItem = (index: number) => {
    const item = props.items[index];

    if (item) {
      // DEV NOTE: seems like `command` can only take id and label
      // so we're passing a type as a label as a workaround
      props.command({
        id: item.id,
        // DEV NOTE: seems like `command` can only take id and label
        // but label can be any JSON object
        label: { type: 'user-mention', name: item.name },
      });
    }
  };

  const upHandler = () => {
    setSelectedIndex(
      (selectedIndex + props.items.length - 1) % props.items.length,
    );
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }

      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }

      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  return (
    <div className="flex flex-col gap-0.5 bg-white rounded-lg border border-gray-200 p-2 relative z-50 overflow-auto w-80 max-h-[25vh]">
      <div className="text-sm text-gray-500 font-semibold px-1.5">Users</div>
      {props.items.length ? (
        props.items.map((item: SuggestionItem, index: number) => {
          const user = users?.find((user) => user.id === item.id);
          return (
            <button
              className={cn(
                index === selectedIndex ? 'is-selected' : '',
                'cursor-pointer',
                'flex flex-row gap-2 items-center px-1.5 py-1',
                'hover:bg-gray-100',
                'rounded-sm',
                'text-sm text-gray-500 font-semibold',
                {
                  'bg-gray-100': index === selectedIndex,
                },
              )}
              key={index}
              onClick={() => selectItem(index)}
            >
              {user && (
                <PaddockAvatar
                  src={user?.avatar}
                  alt={user?.first_name}
                  fallback={user?.first_name}
                />
              )}
              {user && getCallMeBy(user)}
            </button>
          );
        })
      ) : (
        <div
          className={cn(
            'cursor-pointer',
            'flex flex-row gap-2 items-center px-1.5 py-1',
            'hover:bg-gray-100',
            'rounded-sm',
            'text-sm text-black font-semibold',
          )}
        >
          No result
        </div>
      )}
    </div>
  );
});
MentionList.displayName = 'MentionList';
